import { Component, Input, signal, inject } from '@angular/core';
import { CommonModule, DatePipe } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { DataService } from '../../../services/data.service';
import { NgMultiSelectDropDownModule, IDropdownSettings } from 'ng-multiselect-dropdown';

export type StaffStatus	= 'Active' | 'Inactive' | 'On Leave' | 'Resigned' | 'Terminated';
export type staff_type	= 'Teaching' | 'Non-Teaching' | 'Administrative' | 'Support';
export type StaffGender	= 'Male' | 'Female' | 'Other';
export type employment	= 'Permanent' | 'Contractual' | 'Part-time' | 'Guest';

interface Qualification {
  degree:       string;
  subject:      string;
  institution:  string;
  yearOfPassing: number | null;
  percentage:   number | null;
}
interface StaffDocument {
  name:     string;
  fileName: string;
  file:     File | null;
}

@Component({
	selector: 'app-staff-form',
	imports: [CommonModule, FormsModule, NgMultiSelectDropDownModule],
  providers: [DatePipe],
	templateUrl: './staff-form.component.html',
	styleUrl: './staff-form.component.scss',
})
export class StaffFormComponent {
	@Input() action: any;
	user: any = null;

	dataService: any = inject(DataService);
  datePipe = inject(DatePipe);
  currentDate: Date = new Date();

	editingId   = signal<number | null>(null);
  submitted   = false;
  saveSuccess = signal(false);

	base = 'staff';		// important for API calls

  loading = false;
	frmData: any = this.blankForm();

	readonly staff_types:     staff_type[]       = ['Teaching', 'Non-Teaching', 'Administrative', 'Support'];
	readonly employments: 		employment[]  = ['Permanent', 'Contractual', 'Part-time', 'Guest'];
	readonly statusOptions:   StaffStatus[]     = ['Active', 'Inactive', 'On Leave', 'Resigned', 'Terminated'];
	readonly genders:         StaffGender[]     = ['Male', 'Female', 'Other'];
	readonly blood_groups     = ['A+', 'A-', 'B+', 'B-', 'AB+', 'AB-', 'O+', 'O-'];
	readonly religions        = ['Hindu', 'Muslim', 'Christian', 'Sikh', 'Buddhist', 'Jain', 'Other'];
	readonly stateOptions     = ['Andhra Pradesh','Arunachal Pradesh','Assam','Bihar','Chhattisgarh','Delhi','Goa','Gujarat','Haryana','Himachal Pradesh','Jharkhand','Karnataka','Kerala','Madhya Pradesh','Maharashtra','Manipur','Meghalaya','Mizoram','Nagaland','Odisha','Punjab','Rajasthan','Sikkim','Tamil Nadu','Telangana','Tripura','Uttar Pradesh','Uttarakhand','West Bengal','Other'];
  readonly departments 			= this.dataService.departments;
	readonly subjectOptions 	= this.dataService.subjects;

	defaultDocuments: StaffDocument[] = [
		{ name: 'Aadhaar Card',          			fileName: '', file: null },
		{ name: 'PAN Card',              			fileName: '', file: null },
		{ name: 'Qualification Certificate',	fileName: '', file: null },
		{ name: 'Experience Certificate', 		fileName: '', file: null },
		{ name: 'Passport Photo',        			fileName: '', file: null },
	];

	designations: any = this.dataService.designations;
	/*{
		'Teaching':        ['Principal', 'Vice Principal', 'Head of Department', 'Senior Teacher', 'Teacher', 'Assistant Teacher', 'Guest Faculty', 'Lecturer', 'Professor', 'Associate Professor'],
		'Non-Teaching':    ['Librarian', 'Lab Assistant', 'Lab Technician', 'Computer Operator', 'Clerk', 'Accountant', 'Cashier', 'Receptionist', 'Counsellor'],
		'Administrative':  ['Administrator', 'Deputy Administrator', 'HR Manager', 'Office Superintendent', 'Office Assistant', 'Data Entry Operator'],
		'Support':         ['Peon', 'Guard / Security', 'Gardener', 'Sweeper', 'Cook', 'Driver', 'Bus Attendant', 'Electrician', 'Plumber'],
	};*/
	
  ngOnInit() {
    let erp_rem: any = localStorage.getItem("erp_rem");
    if(erp_rem) {
      erp_rem = JSON.parse(erp_rem);
      erp_rem = this.dataService.deCodeUser(erp_rem);
      this.user = erp_rem;
		}

		// Listen to changes continuously
    this.dataService.outerAction.subscribe((value: any) => {
      if(value) {
				if(value.component != 'staff') return;

				this.action = value.action;

				if(this.action == 'edit') {
					this.editingId.set(value.data.id);
				}

				if(value.emp_code) {
					this.frmData.emp_code = value.emp_code;
				}

				this.frmData = value.data ? Object.assign({}, value.data) : this.blankForm();
				this.frmData.reg_date = this.frmData?.reg_date?.substr(0, 10);
				this.frmData.dob = this.frmData?.dob?.substr(0, 10);
				this.frmData.doj = this.frmData?.doj?.substr(0, 10);
				this.frmData.gender = this.frmData?.gender?.trim();
				this.frmData.blood_group = this.frmData?.blood_group?.trim();

				if(typeof this.frmData.documents == 'string') {
					this.frmData.documents = this.dataService.str2json(this.frmData.documents);
				}
				if(typeof this.frmData.qualifications == 'string') {
					this.frmData.qualifications = this.dataService.str2json(this.frmData.qualifications);
				}
				if(typeof this.frmData.subjects == 'string') {
					this.frmData.subjects = this.dataService.str2json(this.frmData.subjects);
				}

        this.dataService.updateOuterAction(null);   //reset value
      }
    });
	}

 // ── Helpers ──────────────────────────────────────────────
	getStatusClass(status: StaffStatus): string {
		return { Active: 'badge-active', Inactive: 'badge-inactive', 'On Leave': 'badge-leave', Resigned: 'badge-resigned', Terminated: 'badge-terminated' }[status] ?? 'bg-secondary';
	}

	getStatusIcon(status: StaffStatus): string {
		return { Active: 'bi-check-circle-fill', Inactive: 'bi-pause-circle-fill', 'On Leave': 'bi-calendar-event-fill', Resigned: 'bi-box-arrow-right', Terminated: 'bi-x-circle-fill' }[status] ?? 'bi-circle';
	}

	getTypeClass(type: staff_type): string {
		return { Teaching: 'type-teaching', 'Non-Teaching': 'type-nonteaching', Administrative: 'type-admin', Support: 'type-support' }[type] ?? '';
	}

	getTypeIcon(type: staff_type): string {
		return { Teaching: 'bi-person-workspace', 'Non-Teaching': 'bi-tools', Administrative: 'bi-briefcase-fill', Support: 'bi-people-fill' }[type] ?? 'bi-person';
	}

	getAvatarColor(id: any): string {
		const colors = ['#1a3a6b','#059669','#d97706','#7c3aed','#0284c7','#dc2626','#0d9488','#be185d'];
		return colors[id % colors.length];
	}

	formatDate(iso: string): string {
		if (!iso) return '—';
		return new Date(iso).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
	}

	calcAge(dob: string): string {
		if (!dob) return '—';
		const d = new Date(dob);
		const age = new Date().getFullYear() - d.getFullYear();
		return `${age} yrs`;
	}

	formatCurrency(n: number | null): string {
		if (!n) return '—';
		return new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 }).format(n);
	}

	private validateForm(): boolean {
		return !!(this.frmData.first_name && this.frmData.last_name && this.frmData.gender
			&& this.frmData.mobile && this.frmData.email && this.frmData.staff_type
			&& this.frmData.department && this.frmData.designation && this.frmData.doj
			&& this.frmData.employment
		);
	}

	private generateEmpId(): string {
		let n: number = this.frmData?.emp_code || 1000;
		return `EMP-${new Date().getFullYear()}-${String(n).padStart(4, '0')}`;
	}

	// get availableDesignations(): string[] {
  //   return this.designations[this.frmData.staff_type] ?? [];
  // }

  // ── Qualifications ───────────────────────────────────────
  addQualification(): void {
    this.frmData.qualifications.push({ degree: '', subject: '', institution: '', yearOfPassing: null, percentage: null });
  }

  removeQualification(i: number): void {
    this.frmData.qualifications.splice(i, 1);
  }

  // ── Subjects (multi-select) ──────────────────────────────
  toggleSubject(sub: string): void {
		if(!this.frmData.subjects) {
			this.frmData.subjects = [];
			this.frmData.subjects.push(sub);
		} else {
			const idx = this.frmData.subjects.indexOf(sub);
			if (idx >= 0) this.frmData.subjects.splice(idx, 1);
			else this.frmData.subjects.push(sub);
		}
  }

  isSubjectSelected(sub: string): boolean {
		if(!this.frmData.subjects) return false;
    return this.frmData.subjects.includes(sub);
  }

	onprofile_photo(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (input.files?.[0]) {
      const reader = new FileReader();
      reader.onload = e => {
				if(e.target?.result && e.total < 40000) {
					this.frmData.profile_photo = e.target?.result as string;
				} else {
					this.frmData.profile_photo = '';
					alert('Error! File size too large.');
				}			
			}
      reader.readAsDataURL(input.files[0]);
    }
  }
	onDocFile(event: Event, doc: StaffDocument): void {
		const input = event.target as HTMLInputElement;
		if (input.files?.[0]) { doc.file = input.files[0]; doc.fileName = input.files[0].name; }
	}

	blankForm() {
		return {
			emp_code: this.generateEmpId(),
			first_name: '', last_name: '', gender: '', 	dob: '', 
			blood_group: '',  mobile: '', email: '', address: '', city: '', state: '', pin_code: '',
			staff_type: 'Support', department: 'itd', designation: 'exe', employment: 'Permanent', 
			doj: '9999-01-01', dol: '9999-01-01', 
			experience: null, subjects: [],
			qualifications: [{ degree: '', subject: '', institution: '', yearOfPassing: null, percentage: null }],
			basic_salary: 0,
			status: 'Active', profile_photo: '', documents: this.defaultDocuments?.map(d => ({ ...d })), 
			remarks: '', is_active: true, reg_date: this.datePipe.transform(this.currentDate, 'yyyy-MM-dd')
		};
	}
  // cancelDelete(): void {
  //   this.showDeleteModal.set(false);
  //   this.deleteTarget.set(null);
  // }
  // executeDelete(): void {
  //   const t = this.deleteTarget();
  //   if (!t) return;
  //   // this.staffList.update(list => list.filter(s => s.id !== t.id));
  //   this.showDeleteModal.set(false);
  //   this.deleteTarget.set(null);
  //   // if (this.currentPage > this.totalPages()) this.currentPage = Math.max(1, this.totalPages());
  // }

	// confirmDelete(staff: any): void {
	// 	this.deleteTarget.set(staff);
	// 	this.showDeleteModal.set(true);
	// }
	openNew(): void {
		this.frmData = this.blankForm();
		this.editingId.set(null);
		this.submitted = false;
		// this.viewMode.set('form');
		window.scrollTo({ top: 0, behavior: 'smooth' });
	}

	openEdit(staff: any): void {
		this.frmData = JSON.parse(JSON.stringify(staff));
		this.editingId.set(staff.id);
		this.submitted = false;
		// this.viewMode.set('form');
		window.scrollTo({ top: 0, behavior: 'smooth' });
	}

	openView(staff: any): void {
		this.editingId.set(staff.id);
		// this.viewMode.set('view');
		window.scrollTo({ top: 0, behavior: 'smooth' });
	}

  doSave() {
		this.submitted = true;
		this.action = null;
		// if (!this.validateForm()) return;

    this.loading = true;
		this.frmData.reg_date = this.datePipe.transform(this.currentDate, 'yyyy-MM-dd');

    this.dataService.addNewRecord(this.base, this.frmData).subscribe({
      next: (response: any) => {
				// set activity log details
				this.dataService.setActivityLog('create', 'staff', 'New staff member "'+this.frmData.first_name+'" added.');
        // console.log('Response received in component:', response);
        this.loading = false;
        this.frmData = this.blankForm();
      },
      error: (error: any) => {
        console.error('Error received in component:', error);
        this.loading = false;
      }
    });
  }

  doUpdate() {
    this.loading = true;
		let myid: any = this.frmData.id;
		delete this.frmData.id;
    // this.frmData.user_role = JSON.stringify(this.frmData.user_role);

    this.dataService.updateRecord(this.base, myid, this.frmData).subscribe({
      next: (response: any) => {
				// set activity log details
				this.dataService.setActivityLog('update', 'staff', 'Update staff member "'+this.frmData.first_name+'" details.');
        // console.log('Response received in component:', response);
        this.loading = false;
        this.frmData = this.blankForm();
			},
      error: (error: any) => {
        console.error('Error received in component:', error);
        this.loading = false;
      }
    });
  }
}
